import { insetPolygon, regionArea, subtractPolygons, type Vec2 } from '@indusketch/geometry';
import {
  buildingCorners,
  createBuildingWalls,
  storeyWallId,
  storeyWallOf,
} from '../building.js';
import { DEFAULT_DOCK } from '../catalog.js';
import { pointAlong } from '../outdoor.js';
import { outwardDoorSwing } from '../history/interiorCommands.js';
import type { LevelDoc } from '../office.js';
import { detectRailingMount } from '../railing.js';
import type { Building, OpeningType, ProjectDocument, Stair, Wall } from '../types.js';
import { stairwellWallSet } from '../office.js';
import { CURRENT_SCHEMA_VERSION } from '../schema/document.js';
import { fillText, modelTexts } from '../texts.js';

export type RawDocument = Record<string, unknown>;

/**
 * One step from `from` to `from + 1`. Migrations operate on untyped JSON because the
 * old shape no longer has a TypeScript type; the result is validated afterwards.
 */
export interface Migration {
  readonly from: number;
  readonly description: string;
  migrate(doc: RawDocument): RawDocument;
}

function isObject(v: unknown): v is RawDocument {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/** v1 → v2: asset-based objects store the model's native size (best guess: current size). */
const addAssetNativeSize: Migration = {
  from: 1,
  description: 'Originalgröße für importierte Modelle ergänzt',
  migrate(doc) {
    const objects = isObject(doc['objects']) ? doc['objects'] : {};
    const migrated: RawDocument = {};
    for (const [id, raw] of Object.entries(objects)) {
      if (
        isObject(raw) &&
        isObject(raw['source']) &&
        raw['source']['kind'] === 'asset' &&
        !('nativeSize' in raw['source'])
      ) {
        migrated[id] = { ...raw, source: { ...raw['source'], nativeSize: raw['dimensions'] } };
      } else {
        migrated[id] = raw;
      }
    }
    return { ...doc, objects: migrated };
  },
};

/** v2 → v3: new entity collections for roof elements and outdoor facilities. */
const addOutdoorCollections: Migration = {
  from: 2,
  description: 'Dachelemente, Straßen, Parkplätze und Zäune ergänzt',
  migrate(doc) {
    return { ...doc, roofElements: {}, roads: {}, parkings: {}, fences: {} };
  },
};

/**
 * v3 → v4: loading docks become gates at floor level with a depressed approach; the former
 * separate "dock leveler" type is merged into them.
 */
const depressedLoadingDocks: Migration = {
  from: 3,
  description: 'LKW-Laderampen auf abgesenkte Zufahrt mit Überladebrücke umgestellt',
  migrate(doc) {
    const openings = isObject(doc['openings']) ? doc['openings'] : {};
    const migrated: RawDocument = {};
    for (const [id, raw] of Object.entries(openings)) {
      if (isObject(raw) && (raw['type'] === 'loading-dock' || raw['type'] === 'dock-leveler')) {
        migrated[id] = { ...raw, type: 'loading-dock', sillHeight: 0, dock: { ...DEFAULT_DOCK } };
      } else {
        migrated[id] = raw;
      }
    }
    return { ...doc, openings: migrated };
  },
};

/** v4 → v5: the single site becomes the first entry of a parcel collection. */
const siteParcels: Migration = {
  from: 4,
  description: 'Grundstück in Flurstücke (mehrere Grundstücke) umgewandelt',
  migrate(doc) {
    const { site, ...rest } = doc;
    const sites: RawDocument = {};
    if (isObject(site) && typeof site['id'] === 'string') sites[site['id']] = site;
    return { ...rest, sites };
  },
};

/** v5 → v6: parcels and halls can be locked against moving; existing ones start unlocked. */
const lockableSitesAndHalls: Migration = {
  from: 5,
  description: 'Sperre für Grundstücke und Hallen ergänzt',
  migrate(doc) {
    return { ...doc, sites: unlocked(doc['sites']), buildings: unlocked(doc['buildings']) };
  },
};

function unlocked(collection: unknown): RawDocument {
  const out: RawDocument = {};
  for (const [id, raw] of Object.entries(isObject(collection) ? collection : {}))
    out[id] = isObject(raw) ? { locked: false, ...raw } : raw;
  return out;
}

/** v6 → v7: every element can be locked against accidental moving and resizing. */
const lockableEverything: Migration = {
  from: 6,
  description: 'Sperre für alle Elemente ergänzt',
  migrate(doc) {
    const out: RawDocument = { ...doc };
    for (const c of [
      'walls',
      'openings',
      'zones',
      'rooms',
      'measurements',
      'annotations',
      'roofElements',
      'roads',
      'parkings',
      'fences',
    ])
      out[c] = unlocked(doc[c]);
    return out;
  },
};

/** v7 → v8: optional background image (site plan / aerial photo). */
const backgroundImage: Migration = {
  from: 7,
  description: 'Hintergrundbild (Lageplan/Luftbild) ergänzt',
  migrate(doc) {
    return { ...doc, background: null };
  },
};

/** v8 → v9: objects can be hidden (e.g. for measuring or a plan without machines). */
const hideableObjects: Migration = {
  from: 8,
  description: 'Objekte ausblendbar',
  migrate(doc) {
    const out: RawDocument = {};
    const objects = doc['objects'];
    for (const [id, raw] of Object.entries(isObject(objects) ? objects : {}))
      out[id] = isObject(raw) ? { hidden: false, ...raw } : raw;
    return { ...doc, objects: out };
  },
};

const withDefaults = (collection: unknown, defaults: RawDocument): RawDocument => {
  const out: RawDocument = {};
  for (const [id, raw] of Object.entries(isObject(collection) ? collection : {}))
    out[id] = isObject(raw) ? { ...defaults, ...raw } : raw;
  return out;
};

/** v9 → v10: office buildings with floors, stairs, and a floor level for interior elements. */
const officesAndFloors: Migration = {
  from: 9,
  description: 'Bürogebäude, Geschosse und Treppen ergänzt',
  migrate(doc) {
    const out: RawDocument = { ...doc, stairs: {} };
    out['buildings'] = withDefaults(doc['buildings'], { kind: 'hall', office: null });
    for (const c of ['walls', 'zones', 'rooms', 'objects', 'measurements', 'annotations'])
      out[c] = withDefaults(doc[c], { level: 0 });
    return out;
  },
};

/** v10 → v11: exit landings on stairs and room types for the room book. */
const landingsAndRoomTypes: Migration = {
  from: 10,
  description: 'Treppenpodeste und Raumarten ergänzt',
  migrate(doc) {
    return {
      ...doc,
      stairs: withDefaults(doc['stairs'], { exitLanding: 0 }),
      rooms: withDefaults(doc['rooms'], { roomType: 'other' }),
    };
  },
};

/** v11 → v12: facade cladding, trim and gate colours per building (the previous fixed look). */
const facadeStyles: Migration = {
  from: 11,
  description: 'Fassadenart sowie Profil- und Torfarbe je Halle ergänzt',
  migrate(doc) {
    const buildings: RawDocument = {};
    const raw = doc['buildings'];
    for (const [id, b] of Object.entries(isObject(raw) ? raw : {})) {
      buildings[id] = isObject(b)
        ? {
            facade: b['kind'] === 'office' ? 'smooth' : 'iso-vertical',
            trimColor: '#4a5059',
            gateColor: '#cdd2d7',
            ...b,
          }
        : b;
    }
    return { ...doc, buildings };
  },
};

/** v12 → v13: walls remember their surface; all existing walls were drawn plain. */
const wallCladding: Migration = {
  from: 12,
  description: 'Oberfläche (glatt / Iso-Paneel) je Wand ergänzt',
  migrate(doc) {
    return { ...doc, walls: withDefaults(doc['walls'], { cladding: 'plain' }) };
  },
};

/** v13 → v14: fence type, colour and gates; existing fences become green welded-mesh fences. */
const fenceStyles: Migration = {
  from: 13,
  description: 'Zaunart, Farbe und Tore je Zaun ergänzt',
  migrate(doc) {
    return {
      ...doc,
      fences: withDefaults(doc['fences'], { kind: 'mesh', color: '#2d4a37', gates: [] }),
    };
  },
};

/**
 * v14 → v15: sliding gates become elements of their own (movable without a fence); gates that
 * sat in a fence keep their place, width, colour and opening side.
 */
const freeGates: Migration = {
  from: 14,
  description: 'Schiebetore als eigene Elemente',
  migrate(doc) {
    const fences: RawDocument = {};
    const gates: RawDocument = {};
    const raw = doc['fences'];
    for (const [id, f] of Object.entries(isObject(raw) ? raw : {})) {
      if (!isObject(f)) {
        fences[id] = f;
        continue;
      }
      const { gates: inFence, ...rest } = f;
      fences[id] = rest;
      const points = Array.isArray(rest['points']) ? (rest['points'] as Vec2[]) : [];
      for (const g of Array.isArray(inFence) ? inFence : []) {
        if (!isObject(g) || typeof g['id'] !== 'string' || points.length < 2) continue;
        const offset = typeof g['offset'] === 'number' ? g['offset'] : 0;
        const width = typeof g['width'] === 'number' ? g['width'] : 6;
        const a = pointAlong(points, offset).point;
        const b = pointAlong(points, offset + width).point;
        gates[g['id']] = {
          id: g['id'],
          name: 'Schiebetor',
          center: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 },
          rotation: Math.atan2(b.y - a.y, b.x - a.x),
          width,
          height: typeof rest['height'] === 'number' ? rest['height'] : 2,
          opensTo: g['opensTo'] === 'end' ? 'right' : 'left',
          color: typeof rest['color'] === 'string' ? rest['color'] : '#4b545c',
          locked: false,
        };
      }
    }
    return { ...doc, fences, gates };
  },
};

const doorSwing: Migration = {
  from: 15,
  description: 'Öffnungsrichtung von Türen (optional, ohne Angabe wie bisher)',
  migrate: (doc) => doc,
};

/** v16 → v17: halls with a freely drawn outline (flat and mono-pitch roofs). */
const freeHallOutlines: Migration = {
  from: 16,
  description: 'Hallen mit frei gezeichnetem Grundriss (ohne Angabe wie bisher rechteckig)',
  migrate(doc) {
    return {
      ...doc,
      buildings: withDefaults(doc['buildings'], { footprint: null, eaveEdge: null }),
    };
  },
};

/** v17 → v18: walkable partial upper floors (Bühnen) in halls. */
const mezzanines: Migration = {
  from: 17,
  description: 'Begehbare Geschossflächen (Bühnen) in Hallen',
  migrate(doc) {
    return { ...doc, mezzanines: {} };
  },
};

/**
 * v18 → v19: doors in the shell of halls and offices and in room walls open outwards (escape
 * direction). Doors with an explicit swing keep it.
 */
const doorsOpenOutward: Migration = {
  from: 18,
  description: 'Türen in Außen- und Raumwänden öffnen nach außen',
  migrate(doc) {
    const walls = isObject(doc['walls']) ? doc['walls'] : {};
    const openings = isObject(doc['openings']) ? doc['openings'] : {};
    const owners = {
      buildings: isObject(doc['buildings']) ? doc['buildings'] : {},
      rooms: isObject(doc['rooms']) ? doc['rooms'] : {},
    } as unknown as Pick<ProjectDocument, 'buildings' | 'rooms'>;
    const out: RawDocument = {};
    for (const [id, o] of Object.entries(openings)) {
      const wall = isObject(o) && typeof o['wallId'] === 'string' ? walls[o['wallId']] : undefined;
      out[id] =
        isObject(o) && o['swing'] === undefined && isObject(wall)
          ? { ...o, ...outwardDoorSwing(owners, wall as unknown as Wall, o['type'] as OpeningType) }
          : o;
    }
    return { ...doc, openings: out };
  },
};

/** v19 → v20: canopies on walls, freely drawn railings and open wall parts. */
const canopiesAndRailings: Migration = {
  from: 19,
  description: 'Vordächer, Geländer und offene Wandteile',
  migrate(doc) {
    return { ...doc, canopies: {}, railings: {} };
  },
};

/**
 * v20 → v21: railings know what they stand on (level, flat roof or a set height). Existing ones
 * are placed onto the mezzanine or office roof below them, as new ones are.
 */
const railingMounts: Migration = {
  from: 20,
  description: 'Geländer stehen auf Geschoss, Bühne oder Flachdach',
  migrate(doc) {
    const raw = isObject(doc['railings']) ? doc['railings'] : {};
    const levels = {
      buildings: isObject(doc['buildings']) ? doc['buildings'] : {},
      mezzanines: isObject(doc['mezzanines']) ? doc['mezzanines'] : {},
    } as unknown as LevelDoc;
    const railings: RawDocument = {};
    for (const [id, r] of Object.entries(raw)) {
      if (!isObject(r) || !Array.isArray(r['points'])) {
        railings[id] = r;
        continue;
      }
      const level = typeof r['level'] === 'number' ? r['level'] : 0;
      railings[id] = { ...r, ...detectRailingMount(levels, r['points'] as Vec2[], level) };
    }
    return { ...doc, railings };
  },
};

/** v21 → v22: fences get pedestrian doors besides sliding gates; existing gates slide. */
const gateKinds: Migration = {
  from: 21,
  description: 'Durchgangstüren in Zaunanlagen',
  migrate(doc) {
    const raw = isObject(doc['gates']) ? doc['gates'] : {};
    const gates: RawDocument = {};
    for (const [id, g] of Object.entries(raw))
      gates[id] = isObject(g) && g['kind'] === undefined ? { ...g, kind: 'sliding' } : g;
    return { ...doc, gates };
  },
};

/**
 * v22 → v23: office exterior walls stand per storey (each can be removed on its own) and
 * upper storeys may be smaller (setbacks). Walls are split, openings move to the storey of
 * their sill, walls removed on every floor become removed pieces, roof terraces become the
 * outline of the top storey.
 */
const storeyWalls: Migration = {
  from: 22,
  description: 'Außenwände von Bürogebäuden je Geschoss, Staffelgeschosse',
  migrate(doc) {
    const rawBuildings = isObject(doc['buildings']) ? doc['buildings'] : {};
    const walls: RawDocument = { ...(isObject(doc['walls']) ? doc['walls'] : {}) };
    const openings: RawDocument = { ...(isObject(doc['openings']) ? doc['openings'] : {}) };
    const buildings: RawDocument = {};
    for (const [id, raw] of Object.entries(rawBuildings)) {
      if (!isObject(raw) || !isObject(raw['office'])) {
        buildings[id] = raw;
        continue;
      }
      const office = raw['office'];
      const floors = Array.isArray(office['floors']) ? office['floors'] : [];
      const omitted = Array.isArray(raw['omittedWalls']) ? (raw['omittedWalls'] as number[]) : [];
      const open = Array.isArray(office['openFloors'])
        ? (office['openFloors'] as { wall: number; level: number; railing: boolean }[])
        : [];
      const openFloors = [
        ...open,
        ...omitted.flatMap((wall) =>
          floors.map((_, level) => ({ wall, level, railing: level > 0 })),
        ),
      ];
      const b = {
        ...raw,
        omittedWalls: [],
        office: { ...office, openFloors, setbacks: [] as unknown[] },
      } as unknown as Building;
      const terraces = Array.isArray(office['terraces']) ? (office['terraces'] as Vec2[][]) : [];
      if (terraces.length > 0 && floors.length >= 2) {
        // Terraces were often traced on the inner wall face: widen them by a wall so they
        // reach the outline, then the top storey keeps the rest.
        const grown = terraces.map((t) => insetPolygon(t, -(b.wallThickness + 0.05)));
        const rest = subtractPolygons(buildingCorners(b), grown)
          .filter((r) => r.holes.length === 0)
          .sort((x, y) => regionArea(y) - regionArea(x))[0];
        const top = floors.length - 1;
        if (rest) {
          (b.office as { setbacks: unknown[] }).setbacks.push({
            level: top,
            outline: rest.outer,
            terrace: false,
          });
          // Pieces removed on that storey referred to its old edges.
          const o = b.office as { openFloors: { level: number }[] };
          o.openFloors = o.openFloors.filter((f) => f.level !== top);
        }
      }
      delete (b.office as Record<string, unknown>)['terraces'];
      buildings[id] = b;
      // Floor elevations, to find each opening's storey from its sill.
      const e = [0];
      for (const f of b.office?.floors ?? [])
        e.push((e[e.length - 1] ?? 0) + f.clearHeight + (b.office?.slabThickness ?? 0));
      const fresh = createBuildingWalls(b);
      const freshIds = new Set(fresh.map((w) => w.id));
      for (const [oid, o] of Object.entries(openings)) {
        if (!isObject(o) || typeof o['wallId'] !== 'string') continue;
        const old = walls[o['wallId']];
        if (!isObject(old) || old['ownerId'] !== id) continue;
        const piece = storeyWallOf(b, o['wallId']);
        const sill = typeof o['sillHeight'] === 'number' ? o['sillHeight'] : 0;
        let level = 0;
        for (let k = 0; k < (b.office?.floors.length ?? 1); k++)
          if (sill >= (e[k] ?? 0) - 1e-6) level = k;
        const target = piece ? storeyWallId(id, level, piece.edge) : null;
        if (target && freshIds.has(target))
          openings[oid] = { ...o, wallId: target, sillHeight: sill - (e[level] ?? 0) };
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- keyed entity maps
        else delete openings[oid];
      }
      for (const [wid, w] of Object.entries(walls))
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete -- keyed entity maps
        if (isObject(w) && w['ownerId'] === id && storeyWallOf(b, wid)) delete walls[wid];
      for (const w of fresh) {
        const old = (doc['walls'] as RawDocument | undefined)?.[w.id];
        walls[w.id] = isObject(old) ? { ...w, color: old['color'] ?? w.color } : w;
      }
    }
    return { ...doc, buildings, walls, openings };
  },
};

/**
 * v23 → v24: the walls of enclosed stairwells become real walls of the storeys they pass, so
 * doors and windows can go into them; the front wall no longer has a fixed door gap.
 */
const stairwellWalls: Migration = {
  from: 23,
  description: 'Treppenhauswände als echte Wände je Geschoss',
  migrate(doc) {
    const stairs = isObject(doc['stairs']) ? doc['stairs'] : {};
    const walls: RawDocument = { ...(isObject(doc['walls']) ? doc['walls'] : {}) };
    const typed = { ...doc, walls } as unknown as ProjectDocument;
    for (const s of Object.values(stairs)) {
      if (!isObject(s) || s['enclosed'] !== true) continue;
      for (const w of stairwellWallSet(typed, s as unknown as Stair)) walls[w.id] = w;
    }
    return { ...doc, walls };
  },
};

/** v24 → v25: columns (Stützen) set by hand in halls and office storeys. */
const columns: Migration = {
  from: 24,
  description: 'Stützen in Hallen und Bürogeschossen',
  migrate(doc) {
    return { ...doc, columns: isObject(doc['columns']) ? doc['columns'] : {} };
  },
};

/** v25 → v26: pictures and logos on walls. */
const wallImages: Migration = {
  from: 25,
  description: 'Bilder und Logos an Wänden',
  migrate(doc) {
    return { ...doc, wallImages: isObject(doc['wallImages']) ? doc['wallImages'] : {} };
  },
};

/** Ordered chain of schema migrations; each step raises the version by one. */
export const MIGRATIONS: readonly Migration[] = [
  addAssetNativeSize,
  addOutdoorCollections,
  depressedLoadingDocks,
  siteParcels,
  lockableSitesAndHalls,
  lockableEverything,
  backgroundImage,
  hideableObjects,
  officesAndFloors,
  landingsAndRoomTypes,
  facadeStyles,
  wallCladding,
  fenceStyles,
  freeGates,
  doorSwing,
  freeHallOutlines,
  mezzanines,
  doorsOpenOutward,
  canopiesAndRailings,
  railingMounts,
  gateKinds,
  storeyWalls,
  stairwellWalls,
  columns,
  wallImages,
];

export type MigrationErrorCode =
  'NOT_AN_OBJECT' | 'MISSING_SCHEMA_VERSION' | 'VERSION_TOO_NEW' | 'MISSING_MIGRATION';

export class MigrationError extends Error {
  constructor(
    readonly code: MigrationErrorCode,
    message: string,
  ) {
    super(message);
    this.name = 'MigrationError';
  }
}

export interface MigrationOutcome {
  readonly document: RawDocument;
  readonly fromVersion: number;
  readonly applied: readonly string[];
}

function isRecord(value: unknown): value is RawDocument {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function migrateDocument(
  raw: unknown,
  migrations: readonly Migration[] = MIGRATIONS,
  targetVersion: number = CURRENT_SCHEMA_VERSION,
): MigrationOutcome {
  const t = modelTexts().parse;
  if (!isRecord(raw)) throw new MigrationError('NOT_AN_OBJECT', t.notObject);
  const version = raw['schemaVersion'];
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
    throw new MigrationError('MISSING_SCHEMA_VERSION', t.missingVersion);
  }
  if (version > targetVersion) {
    throw new MigrationError(
      'VERSION_TOO_NEW',
      fillText(t.tooNew, { version, target: targetVersion }),
    );
  }

  // Work on a copy so a failing migration never leaves the caller's object half-migrated.
  let doc = structuredClone(raw);
  const applied: string[] = [];
  for (let v = version; v < targetVersion; v++) {
    const step = migrations.find((m) => m.from === v);
    if (!step)
      throw new MigrationError('MISSING_MIGRATION', fillText(t.missingMigration, { version: v }));
    doc = { ...step.migrate(doc), schemaVersion: v + 1 };
    applied.push(step.description);
  }
  return { document: doc, fromVersion: version, applied };
}
